import React, { useState, useMemo } from 'react';
import { MapPin, Search, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import { cities, airports, routeCount } from '../mock/content';
import { buildSections } from '../mock/blogContent';
import useSEO from '../hooks/useSEO';

const CitiesIndex = () => {
  const { lang } = useApp();
  const [q, setQ] = useState('');
  const [airport, setAirport] = useState(airports[0].slug);

  const L = {
    de: { title: 'Alle Ziele & Strecken', sub: `Über ${routeCount()} Transfer-Strecken in der ganzen Schweiz – zum Festpreis.`, search: 'Stadt oder Ort suchen...', from: 'Ab Flughafen' },
    en: { title: 'All Destinations & Routes', sub: `Over ${routeCount()} transfer routes across Switzerland – at a fixed price.`, search: 'Search a city or town...', from: 'From airport' },
    fr: { title: 'Toutes les destinations', sub: `Plus de ${routeCount()} trajets de transfert dans toute la Suisse – à prix fixe.`, search: 'Rechercher une ville...', from: 'Depuis l’aéroport' },
    it: { title: 'Tutte le destinazioni', sub: `Oltre ${routeCount()} tratte di transfer in tutta la Svizzera – a prezzo fisso.`, search: 'Cerca una città...', from: 'Dall’aeroporto' },
  }[lang];

  useSEO(`${L.title} | CAB.TAXI Zürich`, L.sub);

  const filtered = useMemo(
    () => cities.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())),
    [q]
  );

  const articleTitle = {
    de: 'Flughafentransfer in der ganzen Schweiz',
    en: 'Airport transfers across Switzerland',
    fr: 'Transferts aéroport dans toute la Suisse',
    it: 'Transfer aeroportuali in tutta la Svizzera',
  }[lang] || 'Airport transfers across Switzerland';
  const rich = buildSections(articleTitle, 'route', lang);

  return (
    <Layout>
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h1 className="text-4xl font-extrabold text-[color:var(--brand-dark)]">{L.title}</h1>
          <p className="mt-4 text-gray-500">{L.sub}</p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 bg-white flex-1">
              <Search className="w-4 h-4 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L.search} className="w-full outline-none text-sm bg-transparent" />
            </div>
            <select value={airport} onChange={(e) => setAirport(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white font-semibold text-[color:var(--brand-dark)]">
              {airports.map((a) => <option key={a.slug} value={a.slug}>{L.from}: {a.name}</option>)}
            </select>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map((c) => (
              <Link key={c.slug} to={`/taxi/${airport}-to-${c.slug}`} className="card-hover bg-white rounded-xl border border-gray-100 p-4 flex items-center justify-between text-sm font-semibold text-[color:var(--brand-dark)]">
                <span className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: 'var(--brand-accent-hover)' }} /> {c.name}</span>
                <ArrowRight className="w-4 h-4 text-gray-300" />
              </Link>
            ))}
          </div>

          {/* Long-form article */}
          <div className="mt-14 max-w-3xl">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[color:var(--brand-dark)] mb-6">{articleTitle}</h2>
            <div className="space-y-9">
              {rich.sections.map((s, i) => (
                <div key={i} data-testid={`cities-section-${i}`}>
                  <h3 className="text-xl font-extrabold text-[color:var(--brand-dark)] mb-3">{s.h}</h3>
                  <div className="space-y-4 text-[15px] leading-relaxed text-gray-700">
                    {s.paras.map((p, j) => <p key={j}>{p}</p>)}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10" data-testid="cities-faq">
              <h3 className="text-xl font-extrabold text-[color:var(--brand-dark)] mb-4">{rich.labels.faq}</h3>
              <div className="space-y-3">
                {rich.faqs.map((f) => (
                  <div key={f.q} className="rounded-xl border border-gray-100 bg-white p-4">
                    <div className="font-bold text-[color:var(--brand-dark)]">{f.q}</div>
                    <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CitiesIndex;
